import { selectCurrentHotelName } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'
import moment from 'moment'

import styles from './AssistDripProposalDialog.module.scss'

const AssistDripProposalDialog = ({
  proposal,
  reasoning,
  onClose,
  onSuccess,
  isHotelSetupCompleted
}) => {
  const { addToast } = useToasts()
  const [isApplying, setIsApplying] = useState(false)
  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)

  if (!proposal) {
    return null
  }

  const handleApprove = async () => {
    if (!isHotelSetupCompleted) {
      addToast(
        'Your initial payment is pending. Please complete it to use drip campaigns.',
        { appearance: 'error', autoDismiss: true }
      )
      return
    }

    setIsApplying(true)
    try {
      const addRes = await campaignApi.addCustomCampaign({
        audienceId: proposal.audienceId,
        deliveryStartDate: proposal.deliveryStartDate,
        deliveryFinishDate: proposal.deliveryFinishDate,
        deliveryName: proposal.deliveryName
      })

      const created = addRes?.item
      const contentId = created?.contentId
      if (!contentId) {
        throw new Error('Campaign was not created')
      }

      await campaignApi.updateCustomCampaign({
        ...created,
        audienceId: proposal.audienceId,
        deliveryStartDate: moment(proposal.deliveryStartDate).format('YYYY-MM-DD'),
        deliveryFinishDate: proposal.deliveryFinishDate
          ? moment(proposal.deliveryFinishDate).format('YYYY-MM-DD')
          : null,
        messageTemplate: proposal.messageTemplate,
        customDeliveryStatus: 'Approved',
        isActive: true,
        medium: proposal.medium || created.medium || 'Text',
        messageMediaUrls: created.messageMediaUrls || []
      })

      addToast('Drip campaign created and started.', {
        appearance: 'success',
        autoDismiss: true
      })
      onSuccess(contentId)
    } catch (err) {
      addToast(err?.message || String(err) || 'Failed to start drip campaign', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <ConfirmationDialog
      title="Suggested drip campaign"
      positiveMessage={isApplying ? 'Starting...' : 'Approve and start'}
      negativeMessage="Not now"
      theme="blacklist"
      hasCloseButton={true}
      isButtonDisabled={isApplying}
      isRejectButtonDisabled={isApplying}
      onConfirm={handleApprove}
      onReject={onClose}
      onClose={onClose}
      maxWidth={560}
      contentAbove={
        <p className={styles.subtitle}>
          Directful Assist analyzed your forecast and audiences. Review the tentative setup
          below.
        </p>
      }
    >
      {isApplying && (
        <div className={styles.loadingOverlay}>
          <LoadingIndicator />
        </div>
      )}
      <div className={styles.details}>
        <div className={styles.row}>
          <span className={styles.label}>Campaign</span>
          <span className={styles.value}>{proposal.deliveryName}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Audience</span>
          <span className={styles.value}>
            {proposal.audienceName}
            {proposal.guestCount > 0 && ` (${proposal.guestCount.toLocaleString()} guests)`}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Duration</span>
          <span className={styles.value}>{proposal.durationLabel}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Est. cost</span>
          <span className={styles.value}>
            ${proposal.estimatedCostUsd?.toFixed(2) ?? '0.00'} USD
            {proposal.complimentaryTextsApplied > 0 &&
              ` (${proposal.complimentaryTextsApplied} complimentary texts applied)`}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Message preview</span>
          <div className={styles.messagePreview}>
            <TextPreviewBubble
              defaultData={{ userName, hotelName }}
              text={proposal.messageTemplate}
              hasLink
            />
          </div>
        </div>
        {reasoning && <p className={styles.reasoning}>{reasoning}</p>}
      </div>
    </ConfirmationDialog>
  )
}

AssistDripProposalDialog.propTypes = {
  proposal: PropTypes.object,
  reasoning: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  isHotelSetupCompleted: PropTypes.bool
}

export default AssistDripProposalDialog
